import type { RouterOptions } from '@ts-rest/core'
import type { ErrorResponse } from '../../../shared/types'
import type {
  ConfirmationTokenEntity,
  CustomerSourceEntity,
  PaymentIntentEntity,
} from '../entities'
import { initContract } from '@ts-rest/core'
import { z } from 'zod'
import { Brand } from '../../../shared/enums'
import { Identifier } from '../../../shared/types'
import {
  BrandSchema,
  CreatePaymentIntent,
  CreateSetupIntent,
  SetupIntentEntitySchema,
  UpdatePaymentIntent,
} from '../entities'

const c = initContract()

const options: RouterOptions = {
  pathPrefix: '/providers/stripe',
  commonResponses: {
    404: c.type<ErrorResponse>(),
    500: c.type<ErrorResponse>(),
  },
}

export const paymentStripeContract = c.router({
  createPaymentIntent: {
    method: 'POST',
    path: '/payment-intents',
    body: CreatePaymentIntent,
    responses: {
      201: c.type<PaymentIntentEntity>(),
    },
  },

  createSetupIntent: {
    method: 'POST',
    path: '/setup-intents',
    body: CreateSetupIntent,
    responses: {
      201: SetupIntentEntitySchema,
    },
  },

  updatePaymentIntent: {
    method: 'PATCH',
    path: '/payment-intents/:intentId',
    body: UpdatePaymentIntent,
    responses: {
      200: c.type<PaymentIntentEntity>(),
    },
  },

  confirmPaymentIntent: {
    method: 'POST',
    path: '/payment-intents/:intentId/confirm',
    pathParams: z.object({
      intentId: z.string(),
    }),
    body: z.object({
      brand: BrandSchema,
      paymentMethod: z.string(),
    }),
    responses: {
      200: c.type<PaymentIntentEntity>(),
      422: c.type<PaymentIntentEntity>(),
    },
  },

  capturePaymentIntent: {
    method: 'POST',
    path: '/payment-intents/:intentId/capture',
    pathParams: z.object({
      intentId: z.string(),
    }),
    body: z.object({
      brand: BrandSchema,
    }).optional(),
    responses: {
      200: c.type<PaymentIntentEntity>(),
    },
  },

  createPaymentSource: {
    method: 'POST',
    path: '/customer/:customerId/create-source',
    pathParams: z.object({
      customerId: z.string(),
    }),
    body: z.object({
      brand: BrandSchema,
    }).optional(),
    responses: {
      200: c.type<CustomerSourceEntity>(),
    },
  },

  createTestPaymentMethod: {
    method: 'POST',
    path: '/customer/:customerId/create-payment-method',
    pathParams: z.object({
      customerId: z.string(),
    }),
    body: z.object({
      brand: BrandSchema,
    }).optional(),
    responses: {
      200: c.type<CustomerSourceEntity>(),
    },
  },

  upsertCustomer: {
    method: 'PUT',
    path: '/customer',
    body: z.object({
      brand: BrandSchema,
      email: z.string().email(),
      name: z.string().optional(),
    }),
    responses: {
      200: z.object({ id: Identifier }),
    },
  },

  createTestConfirmationToken: {
    method: 'POST',
    path: '/test-confirmation-token',
    body: z.object({
      brand: z.nativeEnum(Brand),
      paymentMethodId: z.string(),
    }),
    responses: {
      200: c.type<ConfirmationTokenEntity>(),
    },
  },

}, options)
